import React, { useEffect, useState } from 'react';
import { Download, X } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const InstallPWA: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showInstallBanner, setShowInstallBanner] = useState<boolean>(false);
  const [isIOS, setIsIOS] = useState<boolean>(false);
  const [isStandalone, setIsStandalone] = useState<boolean>(false);

  useEffect(() => {
    // Détecter iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(iOS);

    // Détecter si déjà en mode standalone (installé)
    const standalone = window.matchMedia('(display-mode: standalone)').matches ||
                      (window.navigator as any).standalone === true;
    setIsStandalone(standalone);

    // Écouter l'événement beforeinstallprompt (Android/Chrome)
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      const promptEvent = e as BeforeInstallPromptEvent;
      setDeferredPrompt(promptEvent);

      // Afficher le banner seulement si pas déjà installé et pas déjà refusé
      const dismissed = localStorage.getItem('pwa-install-dismissed');
      if (!dismissed) {
        setShowInstallBanner(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Afficher le banner iOS après 3 secondes si applicable
    if (iOS && !standalone) {
      const dismissed = localStorage.getItem('pwa-install-dismissed');
      if (!dismissed) {
        setTimeout(() => {
          setShowInstallBanner(true);
        }, 3000);
      }
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    // Afficher le prompt d'installation natif
    await deferredPrompt.prompt();

    // Attendre le choix de l'utilisateur
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      console.log('✅ PWA installée');
    } else {
      console.log('❌ Installation refusée');
    }

    // Réinitialiser le prompt
    setDeferredPrompt(null);
    setShowInstallBanner(false);
  };

  const handleDismiss = () => {
    setShowInstallBanner(false);
    localStorage.setItem('pwa-install-dismissed', 'true');
  };

  // Ne rien afficher si déjà installé
  if (isStandalone) {
    return null;
  }

  // Ne rien afficher si le banner est masqué
  if (!showInstallBanner) {
    return null;
  }

  // Banner pour iOS (instructions manuelles)
  if (isIOS && !deferredPrompt) {
    return (
      <div className="fixed bottom-0 left-0 right-0 bg-blue-600 text-white p-4 shadow-2xl z-50 animate-slide-up">
        <div className="max-w-4xl mx-auto flex items-start gap-3">
          <Download className="w-6 h-6 flex-shrink-0 mt-1" />
          <div className="flex-1">
            <h3 className="font-bold text-lg mb-1">Installer l'application</h3>
            <p className="text-sm text-blue-100">
              Pour installer SiteTeacher sur votre iPhone :
            </p>
            <ol className="text-sm text-blue-100 mt-2 space-y-1 list-decimal list-inside">
              <li>Appuyez sur le bouton Partager <span className="inline-block">⎙</span></li>
              <li>Faites défiler et appuyez sur "Sur l'écran d'accueil"</li>
              <li>Appuyez sur "Ajouter"</li>
            </ol>
          </div>
          <button
            onClick={handleDismiss}
            className="text-white hover:text-blue-200 p-1 flex-shrink-0"
            aria-label="Fermer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    );
  }

  // Banner pour Android/Chrome (installation automatique)
  if (deferredPrompt) {
    return (
      <div className="fixed bottom-0 left-0 right-0 bg-blue-600 text-white p-4 shadow-2xl z-50 animate-slide-up">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <Download className="w-6 h-6 flex-shrink-0" />
          <div className="flex-1">
            <h3 className="font-bold text-lg">Installer l'application</h3>
            <p className="text-sm text-blue-100">
              Installez SiteTeacher sur votre appareil pour un accès rapide et une meilleure expérience.
            </p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button
              onClick={handleInstallClick}
              className="bg-white text-blue-600 px-4 py-2 rounded-lg font-semibold hover:bg-blue-50 transition"
            >
              Installer
            </button>
            <button
              onClick={handleDismiss}
              className="text-white hover:text-blue-200 p-2"
              aria-label="Fermer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default InstallPWA;
